/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState, useRef } from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { WeddingsWrapper } from '../components/weddings/styles'
import { services } from '../components/weddings/data'
import Hero from '../components/Hero'
import HeroImage from '../../public/weddings/new_GS_wedding_hero.jpg'
import WhyGlamsquad from '../components/WhyGlamsquad'
import { useMarketContext } from '../components/MarketContext'
import MarketServiceSelector from '../components/MarketServiceSelector/MarketServiceSelector'
import ServiceCardList from '../components/ServiceCardList'
import FAQ from '../components/FAQ'
import HeaderContent from '../components/weddings/HeaderContent'
import { RatingsWrapper } from '../components/home/styles'
import { Header3 } from '../styles/Components'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Autoplay } from 'swiper/modules'
import 'swiper/css/bundle'
import { getFaqsByTag } from '../services/strapi'

// below-fold components — lazy-loaded to reduce initial JS bundle
const AsSeenIn = dynamic(() => import('../components/weddings/AsSeenIn'))
const ImageScroll = dynamic(() => import('../components/ImageScroll'))
const Request = dynamic(() => import('../components/weddings/Request'))
import gridOne from '../../public/weddings/wedding-grid-1.jpg'
import gridTwo from '../../public/weddings/wedding-grid-2.jpg'
import gridThree from '../../public/weddings/wedding-grid-3.jpg'
import gridFour from '../../public/weddings/wedding-grid-4.jpg'
import gridFive from '../../public/weddings/wedding-grid-5.jpg'
import gridSix from '../../public/weddings/wedding-grid-6.jpg'
import gridSeven from '../../public/weddings/wedding-grid-7.jpg'
import gridEight from '../../public/weddings/wedding-grid-8.jpg'
import gridNine from '../../public/weddings/wedding-grid-9.jpg'
import gridTen from '../../public/weddings/wedding-grid-10.jpg'
import gridEleven from '../../public/weddings/wedding-grid-11.jpg'
import gridTwelve from '../../public/weddings/wedding-grid-12.jpg'

const images = [
  gridOne,
  gridTwo,
  gridThree,
  gridFour,
  gridFive,
  gridSix,
  gridSeven,
  gridEight,
  gridNine,
  gridTen,
  gridEleven,
  gridTwelve,
]

const faqUrl = '/faq/category/wedding-hair-makeup'
const faqData = [
  {
    id: 0,
    title: 'How do I book a bridal or wedding service?',
    body: 'Book your own trial through the Glamsquad app or website and our weddings concierge will reach out to you to confirm the details for your wedding trial and start planning your wedding day beauty.',
  },
  {
    id: 1,
    title: 'Who are Glamsquad bridal-certified beauty professionals?',
    body: 'Our talented bridal-certified hairstylists and makeup artists are hand selected and highly vetted. They have an average of 7 years experience in the bridal beauty space and have gone through additional Glamsquad education as well as bridal-specific trend courses to guarantee the highest quality with every Glamsquad wedding experience.',
  },
  {
    id: 2,
    title: 'How far in advance should I book?',
    body: 'We recommend booking 3–6 months in advance to ensure availability of your preferred pros and date. That said, last-minute bookings are possible. Just reach out to our team and they’ll do their best to accommodate bookings with as little as 24–36 hours’ notice.',
  },
  {
    id: 3,
    title: 'Is there a booking minimum?',
    body: 'No, there is no minimum number of people or services required. Both small and large bridal parties are welcome!',
  },
  {
    id: 4,
    title: 'Are hair and makeup services done at the same time?',
    body: 'Yes, hair and makeup are done simultaneously. You can expect a total time in the chair of about 90 minutes. However, if you prefer your services to be back to back, we can definitely make that happen.',
  },
  {
    id: 5,
    title: 'Will I have the same beauty professionals for my trial and my wedding day?',
    body: 'Absolutely! You’re guaranteed the same beauty pros from your trial on your wedding day.',
  },
  {
    id: 6,
    title: 'Why should I book a bridal trial?',
    body: 'Trials differ from standard bookings as they include an in-depth consultation with your bridal-certified beauty professional to discuss all aspects of your wedding day aesthetic and desired bridal beauty look. Your beauty professional will create your desired hairstyle and/or makeup look via a step-by-step process so you can share feedback along the way to ensure you look and feel like the best version of yourself on your most special day.',
  },
  {
    id: 7,
    title: 'What’s included in a bridal trial?',
    body: 'The trial is a dedicated 3-hour session booked separately from the wedding day. The first 90 minutes is with your hairstylist and the second 90 minutes is with your makeup artist. You can use that time to explore multiple looks or perfect a single one, whether for the wedding day itself or an upcoming event like a bridal shower or dress fitting.',
  },
  {
    id: 8,
    title: 'What happens if I don’t like the outcome of my trial?',
    body: 'We always check in with you after your trial to see how everything went. If you’re not satisfied, we’ll make sure your concerns are addressed, so you can head into your big day feeling confident.',
  },
  {
    id: 9,
    title: 'Do you offer airbrush makeup for weddings?',
    body: 'Yes! It’s an additional charge of $25 per person.',
  },
  {
    id: 10,
    title: 'How much should I tip my wedding hair and makeup team?',
    body: 'A 20% gratuity is already built into your all-in pricing, so it’s fully covered. If you’d like to tip additionally on the day of, that is always appreciated but never expected.',
  },
  {
    id: 11,
    title: 'Can Glamsquad travel for a destination wedding?',
    body: 'Yes! As long as Pros are available, we can travel to you. Any travel is factored directly into your all-in quote, so there are no surprises.',
  },
  {
    id: 12,
    title: 'Do you do touch-ups during the reception?',
    body: 'Touch-ups are not included in the standard wedding day services but can be added on separately. The rate is $100/hour per Pro, and we typically send one makeup artist and/or one hairstylist depending on your needs.',
  },
  {
    id: 13,
    title: 'Is there a cancellation fee?',
    body: 'A 50% non-refundable deposit is required at booking to secure your date and your pros. If a member of your bridal party cancels the day of, the cancellation fee still applies to their slot. We recommend gifting that spot to another guest or using the time for additional touch-ups.',
  },
]

// Fallback used when Strapi has no FAQs tagged "weddings". Reshaped to match the
// Strapi FAQ contract ({ question, answer }) so the shared FAQ component renders
// it identically to live content.
const FALLBACK_FAQS = faqData.map((faq) => ({
  id: faq.id,
  question: faq.title,
  answer: [{ type: 'paragraph', children: [{ type: 'text', text: faq.body }] }],
}))

const ratings = [
  {
    quote: '"I did a trial the week before, and they absolutely nailed the look on the very first try. It was exactly what I was hoping for. They made the whole process feel easy and relaxed, which is exactly what you want on a big day."',
    name: 'Ciara',
  },
  {
    quote: '"They were very accommodating as we adjusted the schedule for my bridesmaids and family members who decided to get hair and makeup done. We felt very well taken care of and everybody looked gorgeous!"',
    name: 'Maggie',
  },
  {
    quote: '"Not only is Glamsquad organized and professional, but also extremely responsive to inquiries. Within hours of reaching out, I had secured my appointments."',
    name: 'Kate',
  },
  {
    quote: '"The quality of both the hair and makeup was outstanding—everyone looked flawless in person and in photos, and everything held up beautifully throughout the entire day and night."',
    name: 'Shari',
  },
]

const Weddings = ({ faqs }) => {
  const { markets, selectedMarket, setSelectedMarket } = useMarketContext()

  const faqList = Array.isArray(faqs) && faqs.length > 0 ? faqs : FALLBACK_FAQS

  // service type bar selected state
  const [selectedTab, setSelectedTab] = useState('')

  // observer logic for detecting section scroll into view
  const observer = useRef(null)
  const handleIntersection = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setSelectedTab(entry.target.id)
      }
    })
  }

  useEffect(() => {
    observer.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '-50%',
    })

    const container = document.querySelector('.content-wrapper')
    if (container) {
      Array.from(container.children).forEach((child) =>
        observer.current.observe(child),
      )
    }

    return () => {
      observer.current.disconnect()
    }
  }, [])

  return (
    <>
      <Head>
        <title>Wedding Hair & Makeup Services | GLAMSQUAD</title>
        <meta name="description" content="On-location wedding hair and makeup from bridal-certified GLAMSQUAD pros. Trials, bridal party styling, and day-of touch-ups in 19 cities. Book a trial." />
        <link rel="canonical" href="https://glamsquad.com/weddings" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Wedding Hair & Makeup Services | GLAMSQUAD" />
        <meta property="og:description" content="GLAMSQUAD's bridal-certified pros deliver wedding day hair and makeup to your venue. Trials, bridal party styling, and day-of touch-ups." />
        <meta property="og:url" content="https://glamsquad.com/weddings" />
      </Head>
      <WeddingsWrapper>
        <Hero
          image={HeroImage}
          className="hero-component"
          content={<HeaderContent />}
        />
        <WhyGlamsquad subtitle="Glamsquad brings wedding hair and makeup directly to your home, event venue or hotel suite. Available in 15 US cities, our bridal makeup artists and hair stylists deliver on-site services for brides, bridesmaids, mothers of the bride, and the full wedding party." />
        <MarketServiceSelector
          markets={markets}
          selectedMarket={selectedMarket}
          setSelectedMarket={setSelectedMarket}
          services={services}
          selectedTab={selectedTab}
          marketPrompt="Wedding services in: "
        />
        <ServiceCardList services={services} selectedMarket={selectedMarket} />
        <AsSeenIn />
        <ImageScroll images={images} imageAlt="GLAMSQUAD wedding hair and makeup" />
        <RatingsWrapper>
          <div className="swiper-container-container">
            <Swiper
              modules={[Navigation, Autoplay]}
              slidesPerView={1}
              autoplay={{ delay: 3000, disableOnInteraction: true }}
              navigation={{ nextEl: '.custom-next-button', prevEl: '.custom-prev-button' }}
              className="swiper-container"
            >
              {ratings.map((r, i) => (
                <SwiperSlide key={i}>
                  <div className="rating">
                    <div className="star-container">
                      {new Array(5).fill('').map((_, j) => <img key={j} src="/home/star.svg" width="24" height="24" alt="" aria-hidden="true" />)}
                    </div>
                    <Header3 color="#fff" align="center" margin="0 0 16px 0">
                      {r.quote}
                    </Header3>
                    <Header3 color="#fff" align="center">
                      - {r.name}
                    </Header3>
                  </div>
                </SwiperSlide>
              ))}
              <button className="custom-prev-button" type="button" aria-label="Previous testimonial" />
              <button className="custom-next-button" type="button" aria-label="Next testimonial" />
            </Swiper>
          </div>
        </RatingsWrapper>
        <FAQ faqs={faqList} seeAllUrl={faqUrl} />
        <Request />
      </WeddingsWrapper>
    </>
  )
}

export async function getStaticProps() {
  const faqs = await getFaqsByTag('weddings')
  console.log(faqs)
  return {
    props: { faqs },
    revalidate: 60,
  }
}

export default Weddings
